import React, {createContext, useCallback, useContext, useEffect, useRef, useState} from "react";

// Server-resolved entitlements from GET /api/features: the subscription tier's
// feature set (see FeatureFlagService.TIER_FEATURES) with any per-user override
// applied on top. This only decides what the UI offers — the backend enforces the
// same flags on every gated endpoint, so a stale or tampered value here buys nothing.
const FeatureFlagContext = createContext({
    flags: {}, superAdmin: false, loaded: false, refresh: () => Promise.resolve(),
    campaignFlags: {}, loadCampaign: () => Promise.resolve({}),
});

const cacheKey = username => "featureFlags_" + username;
const campaignCacheKey = campaignId => "campaignFeatures_" + campaignId;

function readUser() {
    try {
        return JSON.parse(sessionStorage.getItem('user'));
    } catch (e) {
        return null;
    }
}

// Instant paint from the last known answer so gated nav items don't pop in a beat
// after every page load. Reconciled with the backend as soon as the fetch lands.
function readCache(username) {
    try {
        return JSON.parse(localStorage.getItem(cacheKey(username))) || {};
    } catch (e) {
        return {};
    }
}

export function FeatureFlagProvider({children}) {
    const user = readUser();
    const superAdmin = Boolean(user?.roles?.includes('ROLE_SUPER_ADMIN'));

    const [flags, setFlags] = useState(() => (user ? readCache(user.username) : {}));
    const [loaded, setLoaded] = useState(false);
    // campaignId -> {ADVANCED_MAPS, SOUNDBOARD, VIDEO_CHAT}: table features, resolved by the
    // server against the campaign OWNER's plan (see useCampaignFeatures).
    const [campaignFlags, setCampaignFlags] = useState({});

    const loadCampaign = useCallback((campaignId) => {
        const userJSON = readUser();
        if (!userJSON || !campaignId) return Promise.resolve({});
        return fetch(`${process.env.REACT_APP_API_PROXY}/features/campaign/${campaignId}`, {
            headers: {'Authorization': 'Bearer ' + userJSON.accessToken}
        })
            .then(res => res.ok ? res.json() : Promise.reject(res))
            .then(data => {
                setCampaignFlags(prev => ({...prev, [campaignId]: data || {}}));
                try { localStorage.setItem(campaignCacheKey(campaignId), JSON.stringify(data || {})); } catch { /* private mode */ }
                return data || {};
            })
            // Same as the account flags: keep what we had rather than lock a table over a blip.
            .catch(() => ({}));
    }, []);

    // Also called by hand from the billing page: entitlements normally only change
    // between sessions, but a Stripe checkout changes them mid-session, and without a
    // re-fetch the user pays and then stares at the same locked UI until a reload.
    const refresh = useCallback(() => {
        const userJSON = readUser();
        if (!userJSON) {
            setFlags({});
            setLoaded(true);
            return Promise.resolve({});
        }

        return fetch(`${process.env.REACT_APP_API_PROXY}/features`, {
            headers: {'Authorization': 'Bearer ' + userJSON.accessToken}
        })
            .then(res => res.ok ? res.json() : Promise.reject(res))
            .then(data => {
                setFlags(data || {});
                localStorage.setItem(cacheKey(userJSON.username), JSON.stringify(data || {}));
                return data || {};
            })
            // Leave the cached flags in place on failure rather than yanking features
            // away mid-session over a blip; the backend still refuses the calls.
            .catch(() => ({}))
            .finally(() => setLoaded(true));
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    // Tables loaded so far, so refresh() (e.g. after a Stripe checkout) unlocks them too.
    const loadedCampaignIdsRef = useRef(new Set());
    const loadCampaignTracked = useCallback((campaignId) => {
        if (campaignId) loadedCampaignIdsRef.current.add(campaignId);
        return loadCampaign(campaignId);
    }, [loadCampaign]);
    const refreshAll = useCallback(() => {
        loadedCampaignIdsRef.current.forEach(id => loadCampaign(id));
        return refresh();
    }, [refresh, loadCampaign]);

    return (
        <FeatureFlagContext.Provider value={{
            flags, superAdmin, loaded, refresh: refreshAll,
            campaignFlags, loadCampaign: loadCampaignTracked,
        }}>
            {children}
        </FeatureFlagContext.Provider>
    );
}

/** True when the user's tier (or an override) grants the feature. Super admins get everything. */
export function useFeature(name) {
    const {flags, superAdmin} = useContext(FeatureFlagContext);
    return superAdmin || flags[name] === true;
}

const TABLE_FEATURES = ['ADVANCED_MAPS', 'SOUNDBOARD', 'VIDEO_CHAT'];

/**
 * A campaign's table-wide features ({ADVANCED_MAPS, SOUNDBOARD, VIDEO_CHAT, loaded}), resolved
 * by the server against the campaign OWNER's plan - a free player at a paying GM's table gets
 * the whole table. Mirrors the server exactly (no super-admin bypass here, unlike useFeature:
 * the server's campaign check has none, so showing a tool it would refuse helps nobody).
 *
 * Until the first answer lands this reports the last cached answer, else everything unlocked:
 * a paying table shouldn't flash lock icons, and the server refuses anything a free table sends.
 */
export function useCampaignFeatures(campaignId) {
    const {campaignFlags, loadCampaign} = useContext(FeatureFlagContext);
    useEffect(() => {
        if (campaignId) loadCampaign(campaignId);
    }, [campaignId, loadCampaign]);

    const live = campaignId ? campaignFlags[campaignId] : null;
    const known = live ?? (campaignId ? readCampaignCache(campaignId) : null);
    const result = {loaded: !!live};
    TABLE_FEATURES.forEach(name => { result[name] = known ? known[name] === true : true; });
    return result;
}

function readCampaignCache(campaignId) {
    try {
        return JSON.parse(localStorage.getItem(campaignCacheKey(campaignId)));
    } catch (e) {
        return null;
    }
}

export function useFeatureFlags() {
    return useContext(FeatureFlagContext);
}
